const dayMs = 1000 * 60 * 60 * 24
const rad = Math.PI / 180
const deg = 180 / Math.PI
const J1970 = 2440588
const J2000 = 2451545
const e = rad * 23.4397

function toJulian(date) {
  return date.valueOf() / dayMs - 0.5 + J1970
}

function fromJulian(j) {
  return new Date((j + 0.5 - J1970) * dayMs)
}

function toDays(date) {
  return toJulian(date) - J2000
}

function rightAscension(l, b) {
  return Math.atan2(Math.sin(l) * Math.cos(e) - Math.tan(b) * Math.sin(e), Math.cos(l))
}

function declination(l, b) {
  return Math.asin(Math.sin(b) * Math.cos(e) + Math.cos(b) * Math.sin(e) * Math.sin(l))
}

function solarMeanAnomaly(d) {
  return rad * (357.5291 + 0.98560028 * d)
}

function eclipticLongitude(M) {
  const C = rad * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M))
  const P = rad * 102.9372
  return M + C + P + Math.PI
}

function sunCoords(d) {
  const M = solarMeanAnomaly(d)
  const L = eclipticLongitude(M)
  return {
    dec: declination(L, 0),
    ra: rightAscension(L, 0),
    M,
    L
  }
}

function siderealTime(d, lw) {
  return rad * (280.16 + 360.9856235 * d) - lw
}

function azimuth(H, phi, dec) {
  return Math.atan2(Math.sin(H), Math.cos(H) * Math.sin(phi) - Math.tan(dec) * Math.cos(phi))
}

function altitude(H, phi, dec) {
  return Math.asin(Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(H))
}

const J0 = 0.0009

function julianCycle(d, lw) {
  return Math.round(d - J0 - lw / (2 * Math.PI))
}

function approxTransit(Ht, lw, n) {
  return J0 + (Ht + lw) / (2 * Math.PI) + n
}

function solarTransitJ(ds, M, L) {
  return J2000 + ds + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L)
}

function hourAngle(h, phi, d) {
  return Math.acos((Math.sin(h) - Math.sin(phi) * Math.sin(d)) / (Math.cos(phi) * Math.cos(d)))
}

function getSetJ(h, lw, phi, dec, n, M, L) {
  const w = hourAngle(h, phi, dec)
  const a = approxTransit(w, lw, n)
  return solarTransitJ(a, M, L)
}

export function getSunPosition(date, lat, lng) {
  const lw = rad * -lng
  const phi = rad * lat
  const d = toDays(date)
  const coords = sunCoords(d)
  const H = siderealTime(d, lw) - coords.ra

  const alt = altitude(H, phi, coords.dec)
  const az = azimuth(H, phi, coords.dec)

  // Convert azimuth from south-based (-pi..pi, west-positive) to north-based (0..360, east-positive)
  const azNorth = (az * deg + 180 + 360) % 360

  return {
    altitudeRad: alt,
    azimuthRad: az,
    altitudeDeg: alt * deg,
    azimuthDeg: azNorth
  }
}

export function getSunTimes(date, lat, lng) {
  const lw = rad * -lng
  const phi = rad * lat
  const d = toDays(date)
  const n = julianCycle(d, lw)
  const ds = approxTransit(0, lw, n)
  const M = solarMeanAnomaly(ds)
  const L = eclipticLongitude(M)
  const dec = declination(L, 0)
  const Jnoon = solarTransitJ(ds, M, L)

  const h0 = -0.833 * rad
  const Jset = getSetJ(h0, lw, phi, dec, n, M, L)
  const Jrise = Jnoon - (Jset - Jnoon)

  return {
    sunrise: fromJulian(Jrise),
    sunset: fromJulian(Jset),
    solarNoon: fromJulian(Jnoon)
  }
}

export function toHonoluluDate(dateStr, minutes) {
  const [year, month, day] = dateStr.split('-').map(Number)
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60

  const utc = Date.UTC(year, month - 1, day, hours + 10, mins)
  return new Date(utc)
}

export function formatHonoluluTime(date) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'Pacific/Honolulu',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date)
}

export function formatMinutes(minutes) {
  const hours = Math.floor(minutes / 60)
  const mins = String(minutes % 60).padStart(2, '0')
  const period = hours >= 12 ? 'PM' : 'AM'
  const twelveHour = hours % 12 === 0 ? 12 : hours % 12
  return `${twelveHour}:${mins} ${period}`
}

export function parseMinutesFromDate(date) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Pacific/Honolulu',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false
  })
  const parts = formatter.formatToParts(date)
  const hour = Number(parts.find((p) => p.type === 'hour')?.value || 0)
  const minute = Number(parts.find((p) => p.type === 'minute')?.value || 0)
  return hour * 60 + minute
}

export function todayHonoluluDateString() {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Pacific/Honolulu',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
  return formatter.format(new Date())
}
